import { useQuery, type Query } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import { VoiceTokenSchema, type VoiceToken } from "@/api/types/voice";

export const voiceTokenKey = ["voice", "token"] as const;

/**
 * GET /api/v2/voice/token — fetches (and, before it expires, silently
 * refreshes) the Twilio Access Token used to build the browser-side
 * `Device` in src/hooks/useVoice.ts.
 *
 * Runs unconditionally (not gated on a contact having a phone, or on a
 * call being in progress) so the ContactDetailPage's Call button can
 * reflect the real `enabled` flag the instant the page loads, rather than
 * showing a stale "disabled" state until the user clicks it. `enabled:
 * false` (Twilio not configured server-side) is a normal, expected
 * response — not an error — so `retry` stays off and no error UI is
 * derived from this query; the button consumes `enabled` directly.
 *
 * Refetches ~60s before the token's TTL elapses (min 30s floor) so a
 * long-lived tab's Device.updateToken() call in useVoice never races an
 * actually-expired token. When disabled (or ttl missing), polling turns
 * itself off — no point hammering an endpoint that's telling us "not
 * configured."
 */
export function useVoiceToken() {
  return useQuery<VoiceToken>({
    queryKey: voiceTokenKey,
    queryFn: async () => {
      const { data } = await apiClient.get("/v2/voice/token");
      return validateResponse(VoiceTokenSchema, data, "GET /v2/voice/token");
    },
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: (query: Query<VoiceToken>) => {
      const data = query.state.data;
      if (!data?.enabled || !data.ttl) return false;
      return Math.max(30_000, (data.ttl - 60) * 1000);
    },
  });
}
